import { useCallback, useContext, useEffect, useRef, useMemo, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import {
  FlightSearchWidgetActions,
  createEmptyFlightSearchWidgetFlightItem,
} from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import {
  StandaloneFlightMenu,
  type FlightSearchWidgetFlightItem,
  type FlightSearchWidgetInitialFocus,
} from 'contexts/Flights/FlightSearchWidget/types'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import FlightSearchAirportInputs from './FlightSearchAirportInputs'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import FlightSearchDateSelect from './FlightSearchDateSelect'
import IconButton from 'LuxKit/Button/IconButton'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import FlightSearchDateSelectSheet from './FlightSearchDateSelectSheet'
import SolidMultiplyIcon from 'LuxKit/Icons/solid/SolidMultiplyIcon'
import FlightSearchMaxFlightsModal from './FlightSearchMaxFlightsModal'
import ModalContext from 'contexts/ModalContext'
import config from 'constants/config'
import { applyFlightSearchInitialFocus } from './applyFlightSearchInitialFocus'

const DATE_FIELD_WIDTH = 144
const INLINE_LAYOUT_MIN_WIDTH = 480
const WIDE_LAYOUT_MIN_WIDTH = 760
const MAX_DATE_FIELD_WIDTH = 304

const InputsWrapper = styled.form`
  container: flight-multi-city-search / inline-size;
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`

const InputRow = styled.div`
  display: grid;
  position: relative;
  gap: ${rem(8)};
  grid-template-areas:
    "location-input"
    "date-input";
  grid-template-columns: minmax(0, 1fr);

  &.with-remove-btn {
    grid-template-areas:
      "location-input location-input"
      "date-input actions";
    grid-template-columns: minmax(0, 1fr) ${rem(40)};
  }

  @container flight-multi-city-search (min-width: ${rem(INLINE_LAYOUT_MIN_WIDTH)}) {
    align-items: center;
    grid-template-areas: "location-input date-input";
    grid-template-columns: minmax(0, 1fr) minmax(${rem(DATE_FIELD_WIDTH)}, max-content);

    &.with-remove-btn {
      grid-template-areas: "location-input date-input actions";
      grid-template-columns: minmax(0, 1fr) minmax(${rem(DATE_FIELD_WIDTH)}, max-content) ${rem(40)};
    }
  }

  @container flight-multi-city-search (min-width: ${rem(WIDE_LAYOUT_MIN_WIDTH)}) {
    grid-template-columns: minmax(0, 1fr) minmax(${rem(DATE_FIELD_WIDTH)}, ${rem(MAX_DATE_FIELD_WIDTH)});

    &.with-remove-btn {
      grid-template-columns: minmax(0, 1fr) minmax(${rem(DATE_FIELD_WIDTH)}, ${rem(MAX_DATE_FIELD_WIDTH)}) ${rem(40)};
    }
  }

  > .location-input-area {
    grid-area: location-input;
    min-width: 0;
  }

  > .date-input-area {
    grid-area: date-input;
    min-width: 0;
  }

  > .actions-area {
    grid-area: actions;
  }
`

const ActionsContainer = styled(Group)`
  @container flight-multi-city-search (min-width: ${rem(INLINE_LAYOUT_MIN_WIDTH)}) {
    &.with-remove-btn {
      margin-inline-end: ${rem(48)};
    }
  }
`

interface Props {
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void
  /**
   * Sets the airports to a 'read only' mode
   * Used when defaulting airport selections such as on flight deal pages
   */
  readOnlyAirports?: boolean
  groupAirportDropdowns?: boolean
  initialFocus?: FlightSearchWidgetInitialFocus
}

function FlightSearchWidgetDesktopMultiCityForm(props: Props) {
  const { onSubmit, readOnlyAirports, groupAirportDropdowns, initialFocus } = props
  const intl = useIntl()
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)
  const hasAppliedInitialFocus = useRef(false)
  const arrivalRefs = useRef<Array<HTMLInputElement | null>>([])
  const departingRefs = useRef<Array<HTMLInputElement | null>>([])
  const departingDateRefs = useRef<Array<HTMLButtonElement | null>>([])
  const formRef = useRef<HTMLFormElement>(null)
  const rowRefs = useRef<Array<HTMLDivElement | null>>([])
  const [datePickerPlacement, setDatePickerPlacement] = useState<'top' | 'bottom'>('bottom')
  const showModal = useContext(ModalContext)

  const { flights } = state

  const [flightDeals] = useFlightDeals()
  const flightDealsMemoized = useMemo(() => (flightDeals || []), [flightDeals])

  const handleAddFlight = useCallback(() => {
    if (flights.length === config.FLIGHTS_MULTI_CITY_MAX_FLIGHTS) {
      showModal<boolean>(<FlightSearchMaxFlightsModal />)
      return
    }

    const item: FlightSearchWidgetFlightItem = createEmptyFlightSearchWidgetFlightItem()

    dispatch({
      type: FlightSearchWidgetActions.ADD_FLIGHT,
      payload: item,
    })
  }, [dispatch, flights.length, showModal])

  const handleRemoveFlight = useCallback(
    (id: string) => {
      dispatch({
        type: FlightSearchWidgetActions.REMOVE_FLIGHT,
        payload: id,
      })
    },
    [dispatch],
  )

  const handleDepartureChange = useCallback((index: number) => {
    arrivalRefs.current[index]?.focus()
  }, [])

  const handleArrivalChange = useCallback((index: number) => {
    departingDateRefs.current[index]?.click()
  }, [])

  const handleDepartingDateChange = useCallback(
    (flightId: string) => {
      const rowIndex = flights.findIndex((flight) => flight.id === flightId)
      departingRefs.current[rowIndex + 1]?.focus()
    },
    [flights],
  )

  const handleDateClose = useCallback(() => {
    dispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU })
  }, [dispatch])

  const handleDateOpen = useCallback((flightId: string) => {
    const rowIndex = flights.findIndex((flight) => flight.id === flightId)
    const rowRect = rowRefs.current[rowIndex]?.getBoundingClientRect()
    if (!rowRect) return
    setDatePickerPlacement(rowRect.top > window.innerHeight - rowRect.bottom ? 'top' : 'bottom')
  }, [flights])

  const showRemoveButton = flights.length > 1

  useEffect(() => {
    if (!initialFocus || hasAppliedInitialFocus.current) {
      return
    }

    const timeoutId = setTimeout(() => {
      const rowIndex = flights.findIndex(flight => flight.id === initialFocus.flightId)
      if (rowIndex < 0) {
        return
      }

      hasAppliedInitialFocus.current = true
      applyFlightSearchInitialFocus(initialFocus, {
        departureAirport: () => departingRefs.current[rowIndex]?.focus(),
        arrivalAirport: () => arrivalRefs.current[rowIndex]?.focus(),
        departureDate: () => departingDateRefs.current[rowIndex]?.click(),
      })
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [flights, initialFocus])

  // Find the index of the selected flight for anchoring the date select sheet to the correct row
  const selectedFlightIndex = useMemo(() => {
    if (!state.selectedFlightId) return 0
    return flights.findIndex((flight) => flight.id === state.selectedFlightId)
  }, [state.selectedFlightId, flights])

  return (
    <InputsWrapper onSubmit={onSubmit} ref={formRef}>
      {flights.map((flight, index) => (
        <InputRow
          key={flight.id}
          className={showRemoveButton ? 'with-remove-btn' : ''}
          ref={(node) => {
            rowRefs.current[index] = node
          }}
        >
          <FlightSearchAirportInputs
            id={flight.id}
            className="location-input-area"
            readOnly={readOnlyAirports}
            arrivalFieldRef={(node) => (arrivalRefs.current[index] = node)}
            departingFieldRef={(node) => (departingRefs.current[index] = node)}
            onDepartureChange={() => handleDepartureChange(index)}
            onArrivalChange={() => handleArrivalChange(index)}
            departingRecentAirports={flights[index - 1]?.arrivalAirport?.cityDesc ? [flights[index - 1]?.arrivalAirport!] : undefined}
            groupAirportDropdowns={groupAirportDropdowns}
            portaledAirportDropdowns
          />

          <FlightSearchDateSelect
            id={flight.id}
            className="date-input-area"
            departingFieldRef={(node) => { departingDateRefs.current[index] = node }}
            onDepartingClick={handleDateOpen}
          />

          {showRemoveButton && (
            <IconButton
              className="actions-area"
              kind="tertiary"
              size="medium"
              data-testid="airportSearchButton"
              onClick={() => handleRemoveFlight(flight.id)}
            >
              <SolidMultiplyIcon />
            </IconButton>
          )}
        </InputRow>
      ))}

      <FlightSearchDateSelectSheet
        id={state.selectedFlightId || state.flights[0]?.id || ''}
        anchorRef={{ current: rowRefs.current[selectedFlightIndex] }}
        placement={datePickerPlacement}
        portaled
        isOpen={state.activeMenu === StandaloneFlightMenu.date && !!state.selectedFlightId}
        onClose={handleDateClose}
        onDateChange={() => state.selectedFlightId && handleDepartingDateChange(state.selectedFlightId)}
        flightDeals={flightDealsMemoized}
      />

      <ActionsContainer
        direction="horizontal"
        verticalAlign="center"
        horizontalAlign="space-between"
        className={`multi-city-actions-row ${
          showRemoveButton ? 'with-remove-btn' : ''
        }`}
      >
        <TextButton
          kind="secondary"
          variant="default"
          startIcon={<LinePlusIcon />}
          size="medium"
          onClick={handleAddFlight}
        >
          {intl.formatMessage({
            id: 'zZNaxnWtkPuAmxA5MOj_',
            defaultMessage: 'Add flight',
            description: 'Button text to add another flight in multi-city flight search',
          })}
        </TextButton>

        <TextButton
          kind="primary"
          size="medium"
          data-testid="airportSearchButton"
          type="submit"
        >
          {intl.formatMessage({
            id: '6D2lT3mm3F43mkH3nYrE',
            defaultMessage: 'Search',
            description: 'Search button text for flight search widget in the flights flow',
          })}
        </TextButton>
      </ActionsContainer>
    </InputsWrapper>
  )
}

export default FlightSearchWidgetDesktopMultiCityForm
